import {
  ChevronDown,
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils.js';
import { Button } from '../../atoms/button.js';
import { Input } from '../../forms/primitives/input.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../molecules/popover.js';
import type { FileNode, FileTreeProps } from './types.js';

function parentPathOf(path: string): string {
  const index = path.lastIndexOf('/');
  return index <= 0 ? '' : path.slice(0, index);
}

function collectOpenFolders(nodes: FileNode[], acc = new Set<string>()): Set<string> {
  for (const node of nodes) {
    if (node.type === 'folder') {
      if (node.open) {
        acc.add(node.path);
      }
      collectOpenFolders(node.children, acc);
    }
  }
  return acc;
}

export function FileTree({
  nodes,
  onCreate,
  onDelete,
  onRename,
  onSelect,
  onUpload,
  uploadPending = false,
  className,
  collapsed = false,
  onCollapseToggle,
}: FileTreeProps) {
  const [openFolders, setOpenFolders] = React.useState<Set<string>>(() =>
    collectOpenFolders(nodes),
  );
  const [editingPath, setEditingPath] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState('');
  const [creatingParentPath, setCreatingParentPath] = React.useState<string | null>(null);
  const [creatingType, setCreatingType] = React.useState<'file' | 'folder' | null>(null);
  const [createValue, setCreateValue] = React.useState('');
  const [deletePath, setDeletePath] = React.useState<string | null>(null);

  React.useEffect(() => {
    const defaults = collectOpenFolders(nodes);
    setOpenFolders((current) => {
      const next = new Set<string>();
      for (const path of current) {
        next.add(path);
      }
      for (const path of defaults) {
        next.add(path);
      }
      return next;
    });
  }, [nodes]);

  const beginRename = React.useCallback((path: string, name: string) => {
    setEditingPath(path);
    setRenameValue(name);
  }, []);

  const commitRename = React.useCallback(() => {
    if (!editingPath) {
      return;
    }

    const nextName = renameValue.trim();
    if (nextName.length > 0) {
      onRename(editingPath, nextName);
    }

    setEditingPath(null);
    setRenameValue('');
  }, [editingPath, onRename, renameValue]);

  const cancelRename = React.useCallback(() => {
    setEditingPath(null);
    setRenameValue('');
  }, []);

  const beginCreate = React.useCallback((parentPath: string, type: 'file' | 'folder') => {
    setCreatingParentPath(parentPath);
    setCreatingType(type);
    setCreateValue('');
  }, []);

  const commitCreate = React.useCallback(() => {
    if (creatingParentPath === null || !creatingType) {
      return;
    }

    const nextName = createValue.trim();
    if (nextName.length > 0) {
      onCreate(creatingParentPath, creatingType, nextName);
    }

    setCreatingParentPath(null);
    setCreatingType(null);
    setCreateValue('');
  }, [createValue, creatingParentPath, creatingType, onCreate]);

  const cancelCreate = React.useCallback(() => {
    setCreatingParentPath(null);
    setCreatingType(null);
    setCreateValue('');
  }, []);

  const toggleFolder = React.useCallback((path: string) => {
    setOpenFolders((current) => {
      const next = new Set(current);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const renderCreateInput = React.useCallback(
    (parentPath: string, depth: number) => {
      if (creatingParentPath !== parentPath || !creatingType) {
        return null;
      }

      return (
        <div className="px-1 py-1" style={{ paddingLeft: depth * 14 + 8 }}>
          <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1">
            {creatingType === 'folder' ? (
              <Folder className="size-4 text-muted-foreground" />
            ) : (
              <FileText className="size-4 text-muted-foreground" />
            )}
            <Input
              autoFocus
              value={createValue}
              onChange={(event) => setCreateValue(event.target.value)}
              onBlur={commitCreate}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitCreate();
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  cancelCreate();
                }
              }}
              className="h-7 border-0 px-0 py-0 shadow-none focus-visible:ring-0"
              placeholder={`New ${creatingType} name`}
              aria-label={`New ${creatingType} name`}
            />
          </div>
        </div>
      );
    },
    [cancelCreate, commitCreate, createValue, creatingParentPath, creatingType],
  );

  const renderNodes = React.useCallback(
    (entries: FileNode[], parentPath: string, depth: number): React.ReactNode => {
      const items: React.ReactNode[] = [];

      for (const node of entries) {
        const isEditing = editingPath === node.path;
        const isActive = Boolean(node.active);

        if (node.type === 'folder') {
          const isOpen = openFolders.has(node.path);
          items.push(
            <div key={node.path}>
              <div
                className={cn(
                  'group flex items-center justify-between rounded-md px-1 py-0.5 text-sm text-foreground',
                  isActive && 'bg-muted',
                )}
                style={{ paddingLeft: depth * 14 + 4 }}
              >
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                  onClick={() => toggleFolder(node.path)}
                  onDoubleClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    beginRename(node.path, node.name);
                  }}
                  aria-expanded={isOpen}
                >
                  {isOpen ? (
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  {isOpen ? (
                    <FolderOpen className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <Folder className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  {isEditing ? (
                    <Input
                      autoFocus
                      value={renameValue}
                      onChange={(event) => setRenameValue(event.target.value)}
                      onBlur={commitRename}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          commitRename();
                        }
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          cancelRename();
                        }
                      }}
                      className="h-7 min-w-0 border-0 px-0 py-0 shadow-none focus-visible:ring-0"
                      aria-label={`Rename ${node.name}`}
                    />
                  ) : (
                    <span className="truncate">{node.name}</span>
                  )}
                </button>

                <div className="ml-2 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    appearance="ghost"
                    className="h-6 w-6"
                    onClick={(event) => {
                      event.stopPropagation();
                      beginCreate(node.path, 'file');
                    }}
                    aria-label={`Add file in ${node.name}`}
                  >
                    <Plus className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    appearance="ghost"
                    className="h-6 w-6"
                    onClick={(event) => {
                      event.stopPropagation();
                      beginCreate(node.path, 'folder');
                    }}
                    aria-label={`Add folder in ${node.name}`}
                  >
                    <Folder className="size-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    appearance="ghost"
                    className="h-6 w-6"
                    onClick={(event) => {
                      event.stopPropagation();
                      beginRename(node.path, node.name);
                    }}
                    aria-label={`Rename ${node.name}`}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                  <Popover
                    open={deletePath === node.path}
                    onOpenChange={(open) => setDeletePath(open ? node.path : null)}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        size="icon"
                        variant="secondary"
                        appearance="ghost"
                        className="h-6 w-6 text-destructive"
                        onClick={(event) => event.stopPropagation()}
                        aria-label={`Delete ${node.name}`}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="end"
                      className="w-56 p-3"
                      onClick={(event) => event.stopPropagation()}
                      aria-label="Confirm delete"
                    >
                      <p className="text-sm font-medium">Delete “{node.name}”?</p>
                      <p className="mt-1 text-xs text-muted-foreground">This cannot be undone.</p>
                      <div className="mt-3 flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          appearance="outline"
                          size="sm"
                          onClick={() => setDeletePath(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            onDelete(node.path);
                            setDeletePath(null);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {isOpen ? (
                <>
                  {renderNodes(node.children, node.path, depth + 1)}
                  {renderCreateInput(node.path, depth + 1)}
                </>
              ) : null}
            </div>,
          );
          continue;
        }

        items.push(
          <div
            key={node.path}
            className={cn(
              'group flex items-center justify-between rounded-md px-1 py-0.5 text-sm text-foreground',
              isActive && 'bg-muted',
            )}
            style={{ paddingLeft: depth * 14 + 4 }}
          >
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
              onClick={() => onSelect(node.path)}
              onDoubleClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                beginRename(node.path, node.name);
              }}
            >
              <span className="w-4" />
              <FileText className="size-4 shrink-0 text-muted-foreground" />
              {isEditing ? (
                <Input
                  autoFocus
                  value={renameValue}
                  onChange={(event) => setRenameValue(event.target.value)}
                  onBlur={commitRename}
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitRename();
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      cancelRename();
                    }
                  }}
                  className="h-7 min-w-0 border-0 px-0 py-0 shadow-none focus-visible:ring-0"
                  aria-label={`Rename ${node.name}`}
                />
              ) : (
                <span className="truncate">{node.name}</span>
              )}
            </button>

            <div className="ml-2 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
              <Button
                type="button"
                size="icon"
                variant="secondary"
                appearance="ghost"
                className="h-6 w-6"
                onClick={(event) => {
                  event.stopPropagation();
                  beginCreate(parentPathOf(node.path), 'file');
                }}
                aria-label={`Add sibling file near ${node.name}`}
              >
                <Plus className="size-3.5" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                appearance="ghost"
                className="h-6 w-6"
                onClick={(event) => {
                  event.stopPropagation();
                  beginRename(node.path, node.name);
                }}
                aria-label={`Rename ${node.name}`}
              >
                <Pencil className="size-3.5" />
              </Button>
              <Popover
                open={deletePath === node.path}
                onOpenChange={(open) => setDeletePath(open ? node.path : null)}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    appearance="ghost"
                    className="h-6 w-6 text-destructive"
                    onClick={(event) => event.stopPropagation()}
                    aria-label={`Delete ${node.name}`}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-56 p-3"
                  onClick={(event) => event.stopPropagation()}
                  aria-label="Confirm delete"
                >
                  <p className="text-sm font-medium">Delete “{node.name}”?</p>
                  <p className="mt-1 text-xs text-muted-foreground">This cannot be undone.</p>
                  <div className="mt-3 flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      appearance="outline"
                      size="sm"
                      onClick={() => setDeletePath(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        onDelete(node.path);
                        setDeletePath(null);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>,
        );
      }

      items.push(
        <React.Fragment key={`create-${parentPath || 'root'}`}>
          {renderCreateInput(parentPath, depth)}
        </React.Fragment>,
      );

      return items;
    },
    [
      beginCreate,
      beginRename,
      cancelRename,
      commitRename,
      deletePath,
      editingPath,
      onDelete,
      onSelect,
      openFolders,
      renameValue,
      renderCreateInput,
      toggleFolder,
    ],
  );

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col rounded-md border border-border bg-background',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between px-2 py-1.5',
          !collapsed && 'border-b border-border',
        )}
      >
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Files
        </span>
        <div className="flex items-center gap-1">
          {!collapsed && (
            <>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                appearance="ghost"
                className="h-7 w-7"
                onClick={() => beginCreate('', 'file')}
                aria-label="Add root file"
              >
                <Plus className="size-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                appearance="ghost"
                className="h-7 w-7"
                onClick={() => beginCreate('', 'folder')}
                aria-label="Add root folder"
              >
                <Folder className="size-4" />
              </Button>
              {onUpload && (
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  appearance="ghost"
                  className="h-7 w-7"
                  onClick={onUpload}
                  disabled={uploadPending}
                  aria-label="Upload file to refs"
                >
                  <Upload className="size-4" />
                </Button>
              )}
            </>
          )}
          {onCollapseToggle && (
            <Button
              type="button"
              size="icon"
              variant="secondary"
              appearance="ghost"
              className="h-7 w-7"
              onClick={onCollapseToggle}
              aria-label={collapsed ? 'Expand navigator' : 'Collapse navigator'}
            >
              <ChevronDown
                className={cn(
                  'size-4 transition-transform duration-200',
                  collapsed && '-rotate-90',
                )}
              />
            </Button>
          )}
        </div>
      </div>

      {!collapsed && (
        <div className="min-h-0 flex-1 overflow-auto p-1">
          {nodes.length > 0 ? (
            renderNodes(nodes, '', 0)
          ) : (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
              No files yet. Use + to add a file or folder.
            </div>
          )}
          {nodes.length === 0 ? renderCreateInput('', 0) : null}
        </div>
      )}
    </div>
  );
}
